import logging from '@wr/logging';
import config from '../../config';
import {CARDINAL_ACTIONS_FAILED_CODES} from '../../consts';
import eventIds from '../../event-ids';
import {fetchProvider} from '../../utils/fetchProvider';
import localiseUrl from '../../utils/localiseUrl';
import {genericErrorHasOccurred} from '../generic-error/action-creators';
import {cyberSourceCardinalInitSucceeded, cyberSourceInitSucceeded, cyberSourceSetSubmitting} from './action-creators';

const getRootHref = () => {
    if (window.location.href.endsWith('/confirmation') || window.location.href.endsWith('/form')) {
        return window.location.href.split('/').slice(0, -1).join('/');
    }

    return window.location.href;
};

const setupCardinal = paymentSessionId => (dispatch, getState) => {
    const logger = logging.createLogger(config.app.name, config.logger.logServerUrl);
    if (!window.Cardinal) {
        logger.error('Retrieving window.Cardinal data failed', {
            error: 'Retrieving window.Cardinal data failed',
            eventId: eventIds['Retrieving window.Cardinal data failed'],
            paymentSessionId
        });
        dispatch(genericErrorHasOccurred(true));
        return () => {};
    }

    const {cyberSourceData: {data: {cardinalToken}}} = getState();

    const onSetupComplete = ({sessionId: cardinalSessionId}) => {
        dispatch(cyberSourceCardinalInitSucceeded(cardinalSessionId));
    };

    const onPaymentsValidation = data => {
        if (CARDINAL_ACTIONS_FAILED_CODES.includes(data.ActionCode)) {
            const error = {
                message: `${data.ErrorDescription} (${data.ErrorNumber})`
            };
            logger.error(`Error in Cardinal payments validation process: ${error.message}`, {
                error: error.message,
                eventId: eventIds['Error in Cardinal payments validation process'],
                paymentSessionId
            });
            dispatch(genericErrorHasOccurred(true));
        }
    };

    window.Cardinal.configure({
        timeout: config.cybersource.timeout,
        extendedTimeout: config.cybersource.extendedTimeout,
        maxRequestRetries: config.cybersource.maxRequestRetries,
        logging: {
            level: config.cybersource.cardinalLogLevel
        }
    });

    window.Cardinal.on('payments.setupComplete', onSetupComplete);
    window.Cardinal.on('payments.validated', onPaymentsValidation);
    window.Cardinal.setup('init', {jwt: cardinalToken});

    const unmountCardinal = () => {
        window.Cardinal.off('payments.setupComplete', onSetupComplete);
        window.Cardinal.off('payments.validated', onPaymentsValidation);
    };

    return unmountCardinal;
};

const loadCyberSourceInit = ({
    languageCode,
    paymentSessionId
}) => dispatch => {
    const url = new URL(
        `/${languageCode}/payments-integrations-webapp/props/getCyberSourceInit`,
        window.location.origin
    );
    url.searchParams.set('paymentSessionId', paymentSessionId);
    return fetchProvider(url.toString())
        .then(data => dispatch(cyberSourceInitSucceeded(data)))
        .catch(error => {
            const logger = logging.createLogger(config.app.name, config.logger.logServerUrl);
            logger.error(`Retrieving CyberSource init data failed: ${error.message}`, {
                error: error.message,
                eventId: eventIds['Retrieving CyberSource init data failed']
            });

            dispatch(genericErrorHasOccurred(true));
            throw error;
        });
};

const loadCyberSourceInitAndSetupCardinal = ({
    languageCode, paymentSessionId
}) => dispatch => {
    const logger = logging.createLogger(config.app.name, config.logger.logServerUrl);
    logger.info('Retrieving CyberSource init data from client', {
        eventId: eventIds['Retrieving CyberSource init data from client'],
        paymentSessionId
    });

    return dispatch(loadCyberSourceInit({
        languageCode,
        paymentSessionId
    }))
        .then(() => dispatch(setupCardinal(paymentSessionId)))
        .catch(error => {
            logger.error(`Retrieving CyberSource init data from client failed: ${error.message}`, {
                error: error.message,
                eventId: eventIds['Retrieving CyberSource init data from client failed']
            });
        });
};


export const paymentValidated = () => new Promise((resolve, reject) => {
    window.Cardinal.on('payments.validated', (data, cardinalDataToken) => {
        if (CARDINAL_ACTIONS_FAILED_CODES.includes(data.ActionCode)) {
            const error = new Error(`${data.ErrorDescription} (${data.ErrorNumber})`);
            return reject(error);
        }

        return resolve({
            cardinalDataToken,
            processorTransactionId: data.Payment.ProcessorTransactionId
        });
    });
}).catch(err => {
    throw new Error(`3ds Validation: ${err.message}`);
});

export const authorizationWith3ds = ({
    payload: {
        afsReply_cardBin: cardBin,
        payerAuthEnrollReply_authenticationTransactionID: authenticationTransactionID,
        payerAuthEnrollReply_paReq: paReq,
        payerAuthEnrollReply_acsURL: acsURL
    }
}) => {
    window.Cardinal.trigger('bin.process', cardBin);
    window.Cardinal.continue('cca',
        {
            AcsUrl: acsURL,
            Payload: paReq
        },
        {
            OrderDetails: {
                TransactionId: authenticationTransactionID
            }
        });

    return paymentValidated();
};

export const enrollAuth = (csrfToken, scope, enrollData) => {
    const url = `${getRootHref()}/auth/enroll`;
    return fetchProvider(url, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'csrf-token': csrfToken
        },
        body: JSON.stringify({
            ...enrollData,
            scope
        })
    }).catch(err => {
        throw new Error(`Auth Enroll: ${err.message}`);
    });
};

export const validateAuth = (csrfToken, scope, validateData) => {
    const authValidateUrl = `${getRootHref()}/auth/validate`;

    return fetchProvider(authValidateUrl, {
        retry: 0,
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'csrf-token': csrfToken
        },
        body: JSON.stringify({
            ...validateData,
            scope
        })
    }).catch(err => {
        throw new Error(`Auth Validate: ${err.message}`);
    });
};

export const paymentProcess = async ({
    csrfToken,
    enrollData,
    authorization3dsProvider,
    languageCode,
    availableLanguageCodes,
    scope
}) => {
    const redirectToStatusUrl = ({redirectUrl}) => {
        window.location.assign(localiseUrl(redirectUrl, languageCode, availableLanguageCodes));
    };

    try {
        const enrollAuthResult = await enrollAuth(csrfToken, scope, enrollData);

        if (enrollAuthResult.continue3ds) {
            const {cardinalDataToken, processorTransactionId} = await authorization3dsProvider(enrollAuthResult);
            const validationResult = await validateAuth(
                csrfToken,
                scope,
                {
                    ...enrollData,
                    cardinalDataToken,
                    processorTransactionId
                }
            );

            return redirectToStatusUrl(validationResult);
        }

        return redirectToStatusUrl(enrollAuthResult.payload);
    } catch (error) {
        throw error;
    }
};


const startAuthorization = (
    tokenError,
    cardDetailsToken,
    csrfToken,
    languageCode,
    availableLanguageCodes,
    shouldUseSavedCard,
    shouldSaveCard
    // eslint-disable-next-line consistent-return
) => async (dispatch, getState) => {
    const logger = logging.createLogger(config.app.name, config.logger.logServerUrl);

    if (tokenError) {
        logger.error(`Microform createToken failed: ${tokenError.message}`, {
            error: `${tokenError.reason}: ${tokenError.message}`,
            eventId: eventIds['Microform createToken failed']
        });
        return dispatch(genericErrorHasOccurred(true));
    }

    const {cyberSourceData: {data: {cardinalSessionId}}} = getState();

    dispatch(cyberSourceSetSubmitting(true));

    const enrollData = {
        cardDetailsToken,
        cardinalSessionId,
        shouldUseSavedCard,
        shouldSaveCard
    };

    try {
        return await paymentProcess({
            csrfToken,
            enrollData,
            languageCode,
            availableLanguageCodes,
            authorization3dsProvider: authorizationWith3ds,
            scope: 'actions/cybersource'
        });
    } catch (error) {
        dispatch(cyberSourceSetSubmitting(false));
        dispatch(genericErrorHasOccurred(true));
        logger.error(`CyberSource authorization process failed: ${error.message}`, {
            error: error.message,
            eventId: eventIds['CyberSource authorization process failed']
        });
    }
};

export default {
    loadCyberSourceInitAndSetupCardinal,
    loadCyberSourceInit,
    setupCardinal,
    startAuthorization
};
